import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Play, RefreshCw, Moon, Sun, ChevronDown, ChevronUp, FileText,
  CheckCircle2, Zap, Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  rhythmApi,
  billingApi,
  decisionsApi,
  dailyPlanApi,
  workItemsApi,
} from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type {
  WorkItem,
  StartMyDayResponse,
  Decision,
  CloseDayResponse,
} from '../types/models';
import {
  deriveCapacity,
  deriveNextAction,
  derivePriorities,
  deriveRisks,
} from '../utils/dashboard';
import NextActionCard from '../components/dashboard/NextActionCard';
import PriorityList from '../components/dashboard/PriorityList';
import RiskStrip from '../components/dashboard/RiskStrip';
import CapacityStrip from '../components/dashboard/CapacityStrip';
import WeeklyFocusKpi from '../components/dashboard/WeeklyFocusKpi';
import DailyBriefingDialog from '../components/briefing/DailyBriefingDialog';
import WhyAffordance from '../components/ai/WhyAffordance';
import CoachingInsightsCard from '../components/CoachingInsightsCard';
import { buildRationaleMap } from '../utils/aiRationale';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// DashboardBriefingPage — Phase 1 dashboard. Leads with four signal-led
// surfaces (NextActionCard / PriorityList / RiskStrip / CapacityStrip).
// Everything denser lives below the fold in DetailPanel.
// ---------------------------------------------------------------------------

export default function DashboardBriefingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dayData, setDayData] = useState<StartMyDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closingDay, setClosingDay] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [closeResult, setCloseResult] = useState<CloseDayResponse | null>(null);
  const [pendingDecisions, setPendingDecisions] = useState<Decision[]>([]);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [briefingDialogOpen, setBriefingDialogOpen] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  // Role gates mirror UnifiedPlanPage / usePlanState semantics. Only the
  // executive (or an Admin acting in their seat) can approve or activate.
  const isEa = user?.role === 'ExecutiveAssistant';
  const planStatus = dayData?.plan?.status;
  const canApprove = !isEa && planStatus === 'Proposed';
  const canActivate = !isEa && planStatus === 'Approved';

  const loadDay = useCallback(async (action: 'start' | 'replan' = 'start') => {
    setError(null);
    setLoading(true);
    try {
      const res = action === 'start' ? await rhythmApi.startMyDay() : await rhythmApi.replan();
      setDayData(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to load today’s plan.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseDay = useCallback(async () => {
    setClosingDay(true);
    try {
      // Close-day reflection is captured by the classic dashboard's guided
      // flow. Here we close without reflection content; the user can fill it
      // in on /reflections after the fact.
      const res = await rhythmApi.closeDay({});
      setCloseResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to close the day.');
    } finally {
      setClosingDay(false);
    }
  }, []);

  const handlePlanTomorrow = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await rhythmApi.planTomorrow();
      setDayData(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to plan tomorrow.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApprove = useCallback(async () => {
    setError(null);
    setTransitioning(true);
    try {
      const updated = await dailyPlanApi.approve(today);
      setDayData((prev) => (prev ? { ...prev, plan: updated } : prev));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to approve the plan.');
    } finally {
      setTransitioning(false);
    }
  }, [today]);

  const handleActivate = useCallback(async () => {
    setError(null);
    setTransitioning(true);
    try {
      const updated = await dailyPlanApi.activate(today);
      setDayData((prev) => (prev ? { ...prev, plan: updated } : prev));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Unable to activate the plan.');
    } finally {
      setTransitioning(false);
    }
  }, [today]);

  const handleCompleteWorkItem = useCallback(async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' as WorkItem['status'] });
      setDayData((prev) => {
        if (!prev) return prev;
        const markDone = (items: WorkItem[]) =>
          items.map((i) => (i.id === id ? { ...i, status: 'Done' as WorkItem['status'] } : i));
        return {
          ...prev,
          overflowItems: markDone(prev.overflowItems),
          carryForwardItems: markDone(prev.carryForwardItems),
          carriedForwardFromYesterday: markDone(prev.carriedForwardFromYesterday),
          scheduledBlocks: prev.scheduledBlocks.map((b) =>
            b.workItem?.id === id
              ? { ...b, workItem: { ...b.workItem, status: 'Done' as WorkItem['status'] } }
              : b,
          ),
        };
      });
    } catch {
      // Best effort. Row stays as-is on failure; no destructive local change.
    }
  }, []);

  useEffect(() => {
    decisionsApi.getPending().then(setPendingDecisions).catch(() => { /* best effort */ });
    billingApi.getStatus()
      .then((s) => {
        if (s?.trialDaysRemaining !== undefined && s?.trialDaysRemaining !== null) {
          setTrialDaysRemaining(s.trialDaysRemaining);
        }
      })
      .catch(() => { /* best effort */ });
  }, []);

  const nextAction = useMemo(() => deriveNextAction(dayData), [dayData]);
  const priorities = useMemo(() => derivePriorities(dayData, 3), [dayData]);
  const risks = useMemo(
    () => deriveRisks(dayData, { pendingDecisionCount: pendingDecisions.length }),
    [dayData, pendingDecisions.length],
  );
  const capacityLine = useMemo(
    () => (dayData ? deriveCapacity(dayData.capacity) : null),
    [dayData],
  );
  const rationaleMap = useMemo(() => buildRationaleMap(dayData), [dayData]);
  const nextActionRationale = nextAction?.workItemId
    ? rationaleMap.get(nextAction.workItemId)
    : undefined;

  const hasBriefingNote = Boolean(
    dayData?.plan?.executiveBriefing?.trim() || dayData?.plan?.handoffNotes?.trim(),
  );

  return (
    <div className="mx-auto max-w-[1200px] p-3 md:p-4">
      {/* Header: date + plan-lifecycle actions */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="font-serif text-2xl font-light tracking-tight">{prettyDate}</h1>
            <WhyAffordance
              rationale={dayData?.plan?.aiRationale}
              secondary={dayData?.aiStrategyAlignment}
              label="Why this shape of day"
            />
          </div>
          {dayData?.plan?.dailyTheme && (
            <p className="mt-1 text-sm text-muted-foreground">{dayData.plan.dailyTheme}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dayData?.plan && (
            <span className="relative">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setBriefingDialogOpen(true)}
              >
                <FileText className="h-4 w-4" />
                Daily briefing
              </Button>
              {hasBriefingNote && (
                <span
                  aria-hidden
                  className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary"
                />
              )}
            </span>
          )}
          {!dayData ? (
            <Button
              onClick={() => loadDay('start')}
              disabled={loading}
              className="gap-1.5"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Start my day
            </Button>
          ) : (
            <>
              {canApprove && (
                <Button
                  size="sm"
                  onClick={handleApprove}
                  disabled={transitioning || loading}
                  className="gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve today's plan
                </Button>
              )}
              {canActivate && (
                <Button
                  size="sm"
                  onClick={handleActivate}
                  disabled={transitioning || loading}
                  className="gap-1.5"
                >
                  <Zap className="h-4 w-4" />
                  Activate plan
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadDay('replan')}
                      disabled={loading}
                      className="gap-1.5"
                    >
                      <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                      Replan
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Sync the calendar and re-allocate focus time around today's meetings. Use whenever a new meeting has landed since the plan was generated.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseDay}
                disabled={closingDay}
                className="gap-1.5"
              >
                <Moon className="h-4 w-4" />
                Close day
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePlanTomorrow}
                disabled={loading}
                className="gap-1.5"
              >
                <Sun className="h-4 w-4" />
                Plan tomorrow
              </Button>
            </>
          )}
        </div>
      </div>

      <DailyBriefingDialog
        open={briefingDialogOpen}
        onClose={() => setBriefingDialogOpen(false)}
        date={today}
        plan={dayData?.plan ?? null}
        onUpdated={(updated) => setDayData((d) => (d ? { ...d, plan: updated } : d))}
      />

      {trialDaysRemaining !== null && trialDaysRemaining >= 0 && trialDaysRemaining <= 14 && (
        <Alert variant={trialDaysRemaining <= 3 ? 'warning' : 'default'} className="mb-4">
          <AlertDescription>
            Free trial: {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining.{' '}
            <Button size="sm" variant="link" className="h-auto p-0" onClick={() => navigate('/billing')}>
              Choose a plan
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {closeResult && (
        <Alert variant="success" className="mb-4">
          <AlertDescription>
            Day closed. {closeResult.itemsToCarryForward} item{closeResult.itemsToCarryForward === 1 ? '' : 's'} carried forward.
          </AlertDescription>
        </Alert>
      )}

      <CoachingInsightsCard />

      {/* Primary composition */}
      {loading && !dayData ? (
        <div className="flex flex-col gap-3">
          <NextActionCard action={null} loading />
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="min-w-0 flex-[2]">
              <PriorityList items={[]} loading />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <RiskStrip risks={[]} loading />
              <CapacityStrip capacity={null} loading />
            </div>
          </div>
        </div>
      ) : dayData ? (
        <div className="flex flex-col gap-3">
          <NextActionCard
            action={nextAction}
            rationale={nextActionRationale?.rationale}
            rationaleStrategyLink={nextActionRationale?.strategyLink}
            onPlanTomorrow={handlePlanTomorrow}
          />

          <div className="flex flex-col items-stretch gap-3 md:flex-row">
            <div className="min-w-0 flex-[2]">
              <PriorityList
                items={priorities}
                rationaleMap={rationaleMap}
                onComplete={handleCompleteWorkItem}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <RiskStrip risks={risks} aiRationale={dayData?.aiRisks} />
              {capacityLine && <CapacityStrip capacity={capacityLine} />}
              <WeeklyFocusKpi />
            </div>
          </div>
        </div>
      ) : (
        // Pre-start empty state: teaches the user what this is, why it's empty,
        // and how to start — per §6 empty-state rules.
        <Card className="rounded-2xl">
          <CardContent className="py-10 text-center">
            <h3 className="mb-1 text-base font-semibold">No plan for today yet</h3>
            <p className="mx-auto mb-4 max-w-lg text-sm text-muted-foreground">
              Start my day pulls your calendar, unfinished work, and strategic drivers into one ordered plan.
              It protects focus time, flags risk, and gives you the single next action to take.
            </p>
            <Button
              size="lg"
              onClick={() => loadDay('start')}
              disabled={loading}
              className="gap-1.5"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Start my day
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Below-the-fold: secondary detail. Collapsed by default. */}
      {dayData && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            className="flex w-full select-none items-center justify-between py-2 text-left"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Today in detail
            </span>
            {detailsOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {detailsOpen && (
            <DetailPanel
              data={dayData}
              pendingDecisionCount={pendingDecisions.length}
              onNavigateWorkItems={() => navigate('/work-items')}
              onNavigateCalendar={() => navigate('/calendar')}
              onNavigateStrategy={() => navigate('/strategy')}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel — everything the classic dashboard showed, with lighter weight
// and links out to the dedicated pages. Mounts only when expanded.
// ---------------------------------------------------------------------------

interface DetailPanelProps {
  data: StartMyDayResponse;
  pendingDecisionCount: number;
  onNavigateWorkItems: () => void;
  onNavigateCalendar: () => void;
  onNavigateStrategy: () => void;
}

function DetailPanel({
  data,
  pendingDecisionCount,
  onNavigateWorkItems,
  onNavigateCalendar,
  onNavigateStrategy,
}: DetailPanelProps) {
  return (
    <div className="flex flex-col gap-3 pt-1">
      {data.aiBriefing && (
        <Card>
          <CardContent className="pt-5">
            <p className="mb-1 text-sm font-semibold">AI briefing</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{data.aiBriefing}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:divide-x sm:divide-border">
            <DetailStat
              label="Scheduled"
              value={data.scheduledBlocks.length}
              suffix="blocks"
              onClick={onNavigateCalendar}
            />
            <DetailStat
              label="Overflow"
              value={data.overflowItems.length}
              suffix={data.overflowItems.length === 1 ? 'item' : 'items'}
              onClick={onNavigateWorkItems}
            />
            <DetailStat
              label="Carry-forward"
              value={data.carryForwardItems.length}
              suffix={data.carryForwardItems.length === 1 ? 'item' : 'items'}
              onClick={onNavigateWorkItems}
            />
            <DetailStat
              label="Decisions pending"
              value={pendingDecisionCount}
              suffix=""
              onClick={onNavigateWorkItems}
            />
            <DetailStat
              label="Strategic drivers"
              value={data.strategicDrivers.length}
              suffix={data.strategicDrivers.length === 1 ? 'active' : 'active'}
              onClick={onNavigateStrategy}
            />
          </div>
        </CardContent>
      </Card>

      {data.aiStrategyAlignment && (
        <Card>
          <CardContent className="pt-5">
            <p className="mb-1 text-sm font-semibold">Strategic alignment</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{data.aiStrategyAlignment}</p>
          </CardContent>
        </Card>
      )}

      {data.plan?.status && (
        <Card>
          <CardContent className="flex items-center gap-2 pt-5">
            <p className="text-sm font-semibold">Plan status</p>
            <Badge variant="secondary">{data.plan.status}</Badge>
            {data.plan.preparedBy && (
              <span className="text-xs text-muted-foreground">Prepared by {data.plan.preparedBy}</span>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DetailStat({
  label,
  value,
  suffix,
  onClick,
}: {
  label: string;
  value: number;
  suffix: string;
  onClick?: () => void;
}) {
  const Wrapper: 'button' | 'div' = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'group min-w-0 flex-1 px-3 text-left',
        onClick ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span
          className={cn(
            'text-lg font-bold tabular-nums transition-colors',
            onClick && 'group-hover:text-primary',
          )}
        >
          {value}
        </span>
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </Wrapper>
  );
}
